import React from 'react';
import { Box, Typography } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { PuzzleOpensDataPoint, PuzzleSolvesDataPoint, PuzzleConfig } from '../types';
import { CustomPuzzleLegend } from './CustomPuzzleLegend';

type PuzzleChartProps = {
  title: string;
  data: PuzzleOpensDataPoint[] | PuzzleSolvesDataPoint[];
  puzzleConfigs: PuzzleConfig[];
  formatDate: (dateString: string) => string;
  hoveredPuzzle: string | null;
  onHover: (dataKey: string) => void;
  onLeave: () => void;
};

export const PuzzleChart = ({
  title,
  data,
  puzzleConfigs,
  formatDate,
  hoveredPuzzle,
  onHover,
  onLeave
}: PuzzleChartProps) => {
  return (
    <Box mt={6}>
      <Typography variant="h5" gutterBottom>
        {title}
      </Typography>
      <Box className="chart-container" position="relative">
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={data.map(d => ({ ...d, displayDate: formatDate(d.date) }))}
            margin={{ top: 5, right: 30, left: 20, bottom: 40 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="displayDate"
              tick={{ fontSize: 12 }}
              interval={Math.floor(data.length / 10)}
            />
            <YAxis />
            <Tooltip
              animationDuration={0}
              labelFormatter={(label) => {
                const point = data.find(d => formatDate(d.date) === label);
                return point ? point.date : label;
              }}
              itemSorter={(item) => -(item.value as number)}
            />
            {puzzleConfigs.map(config => (
              <Line
                key={config.dataKey}
                type="monotone"
                dataKey={config.dataKey}
                stroke={config.color}
                strokeWidth={2}
                name={config.name}
                strokeOpacity={!hoveredPuzzle || hoveredPuzzle === config.dataKey ? 1 : 0.2}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
        <Box position="absolute" bottom="10px" left={0} right={0} display="flex" justifyContent="center">
          <CustomPuzzleLegend
            puzzles={puzzleConfigs}
            onHover={onHover}
            onLeave={onLeave}
          />
        </Box>
      </Box>
    </Box>
  );
};
